import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, RotateCcw, Pencil, Truck, ExternalLink } from "lucide-react";
import { useCart } from "@/lib/cart";
import { scStatus, carrierName } from "@/lib/shipping";
import { computeLayout, initialDesign, unitPrice, basePrice, discounted } from "@/lib/layout";
import { MyProfile } from "@/components/account/MyProfile";
import { GuideButton } from "@/components/GuideButton";
import { MyStamps } from "@/components/account/MyStamps";
import { useAuth } from "@/lib/auth";
import { api, API, eur, fmtDate, STATUS_LABELS, downloadFile, errMsg } from "@/lib/api";

export function StatusBadge({ s }) {
  const c = { pagato: "bg-green-100 text-green-800", in_produzione: "bg-blue-100 text-blue-800", spedito: "bg-violet-100 text-violet-800",
    completato: "bg-slate-200 text-slate-800", annullato: "bg-rose-100 text-rose-800" }[s] || "bg-amber-100 text-amber-800";
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${c}`} data-testid="order-status-badge">{STATUS_LABELS[s] || s}</span>;
}

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const cart = useCart();
  const navigate = useNavigate();
  useEffect(() => { if (user) api.get("/my/orders").then((r) => setOrders(r.data)); }, [user]);
  if (user === false) return <Navigate to="/accedi?next=/account" replace />;
  if (!user) return null;

  const reorder = async (it, edit) => {
    try {
      if (it.kind === "pad") {
        const { data: pad } = await api.get(`/products/${it.slug}`);
        const tiers = pad.config?.qty_tiers || [];
        cart.add({ product_id: pad.id, slug: pad.slug, name: pad.name, sku: pad.sku, kind: "pad", image: pad.image, quantity: it.quantity,
          ink_color: pad.ink_colors.includes(it.ink_color) ? it.ink_color : pad.ink_colors[0], unit_price: discounted(pad.price, tiers, it.quantity),
          base_price: pad.price, tiers, design: {}, layout: null, pads: 0, pad_price: 0 });
        toast.success("Aggiunto al carrello");
        return navigate("/carrello");
      }
      if (!it.design?.lines?.length && !it.design?.graphics?.length) throw new Error("Design non disponibile per questo ordine");
      const { data: product } = await api.get(`/products/${it.slug}`);
      const design = { ...initialDesign(product), ...it.design };
      if (!product.ink_colors.includes(it.ink_color)) throw new Error("Colore inchiostro non più disponibile");
      if (edit) return navigate(`/timbro/${product.slug}`, { state: { reorder: { design, ink_color: it.ink_color, quantity: it.quantity, pads: it.pads || 0 } } });
      cart.add({
        product_id: product.id, slug: product.slug, name: product.name, quantity: it.quantity, ink_color: it.ink_color,
        unit_price: unitPrice(product, design, it.quantity), base_price: basePrice(product, design), tiers: product.config.qty_tiers || [],
        design, layout: computeLayout(product, design),
        pads: product.config.pad_price > 0 ? it.pads || 0 : 0, pad_price: product.config.pad_price || 0, pad_name: product.config.pad_name || "Tampone di ricambio",
      });
      toast.success("Timbro aggiunto al carrello");
      navigate("/carrello");
    } catch (e) { toast.error(e.response ? errMsg(e) : e.message); }
  };

  const pay = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/pay`);
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-8">
      <div>
        <div className="font-mono text-[11px] tracking-widest text-slate-500">IL MIO ACCOUNT</div>
        <h1 className="text-5xl font-extrabold uppercase">Ciao {user.name}</h1>
        <p className="text-sm text-slate-600">{user.email}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <GuideButton testid="account-guide-btn" />
          {user.cassa_client_id && <Link to="/account/negozio" data-testid="account-shop-link" className="press inline-flex items-center h-11 px-5 rounded-md bg-brand-navy text-white font-bold">I miei acquisti in negozio e prenotazioni</Link>}
          <Link to="/account/password" data-testid="account-password-link" className="press inline-flex items-center h-11 px-4 rounded-md border text-sm font-semibold">Cambia password</Link>
        </div>
      </div>
      <MyStamps />
      <MyProfile />
      <MyPriceList />
      <h2 className="text-2xl font-bold uppercase">I miei ordini</h2>
      {orders === null ? <p className="text-slate-500">Caricamento…</p> : orders.length === 0 ? (
        <p className="text-slate-500" data-testid="no-orders">Non hai ancora effettuato ordini.</p>
      ) : (
        <div className="space-y-3" data-testid="my-orders-list">
          {orders.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid={`my-order-${o.number}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3"><span className="font-mono font-semibold">{o.number}</span><StatusBadge s={o.status} /></div>
                <div className="text-sm text-slate-500">{fmtDate(o.created_at)} · <span className="font-bold text-brand-navy">{eur(o.total)}</span></div>
              </div>
              <TrackingBox o={o} />
              <button data-testid={`my-order-document-${o.number}`} onClick={() => downloadFile({ url: `/my/orders/${o.id}/document` }, `ordine-${o.number}.pdf`).catch((e) => toast.error(errMsg(e)))}
                className="press inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-brand-navy text-xs font-semibold"><FileDown className="w-3.5 h-3.5" /> Documento ordine (PDF)</button>
              {o.items.map((it, idx) => (
                <div key={idx} className="flex flex-wrap items-center justify-between gap-2 text-sm border-t pt-2">
                  {it.preview_image && <img src={`${API}/files/${it.preview_image}`} alt="Anteprima timbro" className="w-56 max-w-full h-20 object-contain border rounded bg-white p-1" data-testid={`my-order-preview-img-${o.number}-${idx}`} />}
                  <span>{it.name} × {it.quantity}{it.details?.length > 0 && <span className="block text-xs text-slate-500">{it.details.join(" · ")}</span>}{it.pads > 0 && <span className="text-slate-500"> + {it.pads} {it.pad_name}</span>}</span>
                  {it.kind === "pad" && (
                    <button data-testid={`my-order-reorder-${o.number}-${idx}`} onClick={() => reorder(it, false)}
                      className="press flex items-center gap-1 h-8 px-3 rounded-md bg-brand-navy text-white text-xs font-semibold"><RotateCcw className="w-3.5 h-3.5" /> Riordina</button>
                  )}
                  {it.has_layout && <span className="flex flex-wrap items-center gap-3">
                  <button data-testid={`my-order-reorder-${o.number}-${idx}`} onClick={() => reorder(it, false)}
                    className="press flex items-center gap-1 h-8 px-3 rounded-md bg-brand-navy text-white text-xs font-semibold"><RotateCcw className="w-3.5 h-3.5" /> Riordina</button>
                  <button data-testid={`my-order-reorder-edit-${o.number}-${idx}`} onClick={() => reorder(it, true)}
                    className="press flex items-center gap-1 h-8 px-3 rounded-md border border-slate-300 text-xs font-semibold hover:border-brand-navy"><Pencil className="w-3.5 h-3.5" /> Riordina e modifica</button>
                  <button data-testid={`my-order-preview-${o.number}-${idx}`} onClick={() => downloadFile({ url: `/my/orders/${o.id}/items/${idx}/preview` }, `${o.number}-${idx + 1}.pdf`)}
                    className="flex items-center gap-1 text-brand-navy hover:underline text-xs font-semibold"><FileDown className="w-3.5 h-3.5" /> Anteprima PDF</button>
                  </span>}
                </div>
              ))}
              {["paid", "deferred"].includes(o.payment_status) && (
                <div className="flex flex-wrap items-center gap-3 border-t pt-3 text-xs">
                  {o.withdrawal_at ? <span className="font-semibold text-amber-700" data-testid={`my-order-withdrawn-${o.number}`}>Recesso inviato il {fmtDate(o.withdrawal_at)}</span> : (
                    <Link to={`/recesso/modulo?ordine=${o.number}`} data-testid={`my-order-withdraw-${o.number}`} className="font-semibold text-brand-navy underline">Recedere dal contratto qui</Link>
                  )}
                </div>
              )}
              {o.payment_status === "pending" && o.status !== "annullato" && (
                <button data-testid={`my-order-pay-${o.number}`} onClick={() => pay(o)} className="press h-9 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold">Completa il pagamento</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MyPriceList() {
  const [pl, setPl] = useState(null);
  useEffect(() => { api.get("/my/price-list").then((r) => setPl(r.data)).catch(() => setPl(null)); }, []);
  if (!pl) return null;
  const dl = (fmt) => downloadFile({ url: "/my/price-list/file", params: { fmt } }, `listino-halfa-stamps.${fmt}`).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-xl border-2 border-brand-navy bg-white p-5 flex flex-wrap items-center justify-between gap-4 shadow-[5px_5px_0_#EDD93F]" data-testid="my-price-list">
      <div>
        <div className="font-mono text-[11px] tracking-widest text-slate-500">IL TUO LISTINO RISERVATO</div>
        <div className="font-display text-2xl font-bold uppercase text-brand-navy">{pl.name}</div>
        <div className="text-xs text-slate-500">{pl.count} prodotti{pl.valid_until ? ` · valido fino al ${new Date(pl.valid_until).toLocaleDateString("it-IT")}` : ""}</div>
      </div>
      <div className="flex gap-2">
        <button data-testid="my-price-list-pdf" onClick={() => dl("pdf")} className="press h-10 px-4 rounded-md bg-brand-navy text-white text-sm font-semibold flex items-center gap-2"><FileDown className="w-4 h-4" /> PDF</button>
        <button data-testid="my-price-list-csv" onClick={() => dl("csv")} className="press h-10 px-4 rounded-md border border-brand-navy text-sm font-semibold flex items-center gap-2"><FileDown className="w-4 h-4" /> CSV</button>
      </div>
    </div>
  );
}

function TrackingBox({ o }) {
  if (o.tracking) return (
    <div className="rounded-lg border-2 border-brand-navy bg-brand-yellow/20 p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`my-order-tracking-${o.number}`}>
      <div className="flex items-center gap-3">
        <Truck className="w-5 h-5 text-brand-navy" />
        <div>
          <div className="font-mono text-[11px] tracking-widest text-slate-600">TRACCIAMENTO{o.tracking.carrier ? ` · ${carrierName(o.tracking.carrier)}` : ""}</div>
          <div className="font-mono font-bold text-brand-navy select-all" data-testid={`my-order-tracking-number-${o.number}`}>{o.tracking.number}</div>
          {scStatus(o.tracking.status) && <div className="text-xs text-slate-600">{scStatus(o.tracking.status)}</div>}
        </div>
      </div>
      {o.tracking.url?.startsWith("https://") && (
        <a href={o.tracking.url} target="_blank" rel="noreferrer" data-testid={`my-order-tracking-link-${o.number}`} className="press h-9 px-4 rounded-md bg-brand-navy text-white text-xs font-semibold flex items-center gap-1.5">Traccia la spedizione <ExternalLink className="w-3.5 h-3.5" /></a>
      )}
    </div>
  );
  if (!["pagato", "ricevuto", "in_produzione"].includes(o.status)) return null;
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-3 flex items-center gap-3 text-sm text-slate-500" data-testid={`my-order-tracking-pending-${o.number}`}>
      <Truck className="w-5 h-5" /> Il codice di tracciamento comparirà qui appena il pacco viene affidato al corriere.
    </div>
  );
}
